import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { APPS } from "@/data/apps";
import { useI18n } from "@/i18n";

const card = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 },
  }),
};

export const AppsGrid = () => {
  const { t } = useI18n();
  const a = t.apps;

  return (
    <section
      id="apps"
      data-testid="apps-section"
      className="relative mx-auto max-w-[1400px] px-6 lg:px-10 py-28 lg:py-36"
    >
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
        <div>
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gradient-to-r from-blue-400 to-cyan-400" />
            <span className="text-xs uppercase tracking-[0.35em] text-white/50">
              {a.caption}
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-light tracking-tight text-white max-w-2xl leading-[1.05]">
            {a.heading1}
            <br />
            <span className="text-gradient font-normal">{a.heading2}</span>
          </h2>
        </div>
        <p className="text-white/50 max-w-sm text-sm leading-relaxed">
          {a.intro}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {APPS.map((app, i) => {
          const { Icon } = app;
          const copy = a.items[app.id];
          return (
            <motion.a
              key={app.id}
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              custom={i}
              variants={card}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              data-testid={`app-card-${app.id}`}
              className={`glass-card group relative rounded-2xl p-8 flex flex-col min-h-[260px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${app.span}`}
            >
              <div className="flex items-start justify-between mb-8">
                <div className="flex items-center justify-center w-12 h-12 rounded-xl border border-white/10 bg-white/[0.03] group-hover:border-blue-400/50 transition-colors duration-500">
                  <Icon
                    className="w-6 h-6 text-white/70 group-hover:text-blue-300 transition-colors duration-500"
                    strokeWidth={1.4}
                  />
                </div>
                <span className="font-display text-sm font-light text-white/25">
                  {app.index}
                </span>
              </div>

              <span className="text-[10px] uppercase tracking-[0.25em] text-blue-300/80 mb-3">
                {copy.category}
              </span>
              <h3 className="font-display text-xl font-medium text-white leading-snug mb-3">
                {copy.name}
              </h3>
              <p className="text-sm text-white/50 leading-relaxed">
                {copy.description}
              </p>

              <div className="mt-auto pt-6 flex items-center justify-between">
                <span className="text-xs text-white/40 group-hover:text-white/70 transition-colors duration-300">
                  {app.domain}
                </span>
                <span className="flex items-center justify-center w-9 h-9 rounded-full border border-white/10 group-hover:border-blue-400/50 group-hover:bg-blue-500/10 transition-colors duration-300">
                  <ArrowUpRight className="w-4 h-4 text-white/60 group-hover:text-blue-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
                </span>
              </div>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
};
